import type { RectangularArray } from "building-types/electrical/electricalLayoutTypes";
import { RoomBoundaryType, SpacingType } from "building-types/electrical/electricalLayoutTypesEnum";
import { ControllerSwitchType, ControllerType } from "building-types/electrical/electricalTypesEnum";
import type { Load } from "building-types/electrical/generatedElectricalTypes";
import type { Position } from "building-types/rooms/roomPosition";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { poleOfInaccessibilityPolygon } from "geom/label";
import { mat3Dot, mat3DotNoTranslate, mat3Inverse } from "geom/mat3";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2MergeCollinearEdges,
  polyLine2SetCCW,
  polyLine2Transform,
} from "geom/polyLine2";
import { polyLine2MaximalRectangle } from "geom/polyLine2MaximalRect";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist } from "geom/vec2";
import { candidateFilteringCriteriaNew } from "../controllerSelection/controllerSelectionShared";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { getParams, getPositionsFromParams, makeControllerSelection } from "./linearArray";

export function rectangularArray({
  layoutType: type,
  controllerPlacementType,
  endControllerPlacementType,
  loadType,
  xSpacing,
  xSpacingType,
  xShift,
  xQuantity,
  ySpacing,
  ySpacingType,
  yShift,
  yQuantity,
  trimToRoom,
  minDistToWall,
  roomBoundaryType,
}: RectangularArray): ElectricalLayoutFunc {
  const resolvedRoomBoundaryType = roomBoundaryType === undefined ? RoomBoundaryType.BOUNDING_BOX : roomBoundaryType;

  return async (opts: ElectricalLayoutOpts) => {
    const { room, roomOutlines, controllerSelectionFunc, electricalItemCatalogSettings } = opts;
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[loadType];
    const insideFramingOutline = roomOutlines.INSIDE_OF_MASS_IGNORE_VOID;
    assertIsNotNullOrUndefined(insideFramingOutline);
    // For a component instance the inside outline is the source room's outline run through the
    // instance transform (and re-normalized to CCW). A symmetric room reflects to an identical
    // outline, so computing the array from that geometry loses the mirror. Instead compute the
    // whole array in the source room's own frame by mapping the outline back through the inverse
    // transform, then map the resulting load positions/normals forward again. The matrix carries
    // the full reflection/rotation, so the array mirrors and rotates with the instance exactly.
    const componentTransform = room.componentTransform;
    const componentMatrix = componentTransform?.matrix;
    const inverseMatrix = componentTransform
      ? componentTransform.inverseMatrix ?? mat3Inverse(componentTransform.matrix)
      : undefined;
    // Copy so the in-place operations below do not mutate the shared inside outline. For a
    // component instance, map the outline into the source frame and restore CCW (inverting a mirror
    // flips winding) before merging collinear edges.
    let outline = polyLine2Copy(insideFramingOutline);
    if (inverseMatrix) {
      outline = polyLine2Transform(outline, inverseMatrix);
      outline = polyLine2SetCCW(outline);
    }
    outline = polyLine2MergeCollinearEdges(outline);
    if (resolvedRoomBoundaryType === RoomBoundaryType.MAX_RECTANGLE) {
      const { maxRectAsPolyLine2 } = polyLine2MaximalRectangle(outline);
      outline = maxRectAsPolyLine2;
    }
    const maxLengthIndex = getMaxLengthEdgeIndex(outline);
    if (maxLengthIndex === -1) {
      return [];
    }
    const [originalOutlineX, originalOutlineY] = polyLine2EdgeEval(outline, maxLengthIndex, 0.5, true);
    const [nx, ny] = polyLine2EdgeNormal(outline, maxLengthIndex, true);
    let [tx, ty] = polyLine2EdgeTangent(outline, maxLengthIndex, false);
    // When a non-zero shift is specified, ensure the tangent direction is consistent
    // with the shift so that the shift is applied consistently regardless of outline vertex winding.
    // Without this, mirrored rooms can reverse the tangent, causing the shift to be applied
    // in the opposite direction. We only ensure consistency when shift is present to avoid
    // changing load ordering (and thus wiring topology) for zero-shift layouts.
    // Flipping the tangent also reverses the perpendicular direction (ty, -tx) used
    // for the minor axis, which swaps the minor axis segment endpoints. To keep
    // yShift correct we negate it when the flip occurs.
    let tangentCanonicalized = false;
    if ((xShift || yShift) && (ty > 0 || (Math.abs(ty) < DIST_EPSILON && tx < 0))) {
      tx = -tx;
      ty = -ty;
      tangentCanonicalized = true;
    }
    const { sourceX, sourceY } = getArraySourcePoint(originalOutlineX, originalOutlineY, nx, ny, outline);

    const { invalidIntersection, sx, sy, ex, ey } = getLineFromOutlineRayIntersections(
      outline,
      sourceX,
      sourceY,
      tx,
      ty,
    );
    if (invalidIntersection) {
      return [];
    }
    const halfDist = vec2Dist(sx, sy, ex, ey) / 2;
    const {
      invalidIntersection: invalidIntersection2,
      sx: sx2,
      sy: sy2,
      ex: ex2,
      ey: ey2,
    } = getLineFromOutlineRayIntersections(outline, sourceX, sourceY, ty, -tx);
    if (invalidIntersection2) {
      return [];
    }
    const xResolvedShift = xShift || 0;
    const xResolvedMinDistToWall = xShift === undefined ? minDistToWall || 0 : 0;
    const xResolvedSpacingType = xSpacingType === undefined ? SpacingType.SEGMENT_END_POINTS : xSpacingType;

    const yResolvedShift = (yShift || 0) * (tangentCanonicalized ? -1 : 1);
    const yResolvedMinDistToWall = yShift === undefined ? minDistToWall || 0 : 0;
    const yResolvedSpacingType = ySpacingType === undefined ? SpacingType.SEGMENT_END_POINTS : ySpacingType;

    const majorAxisParams = getParams(
      xQuantity,
      xSpacing,
      xResolvedSpacingType,
      sx,
      sy,
      ex,
      ey,
      xResolvedShift,
      xResolvedMinDistToWall,
    );

    const minorAxisParams = getParams(
      yQuantity,
      ySpacing,
      yResolvedSpacingType,
      sx2,
      sy2,
      ex2,
      ey2,
      yResolvedShift,
      yResolvedMinDistToWall,
    );

    if (tangentCanonicalized) {
      majorAxisParams.reverse();
      minorAxisParams.reverse();
    }

    const test: Position[] = [];
    for (let i = 0; i < majorAxisParams.length; i++) {
      const reverse = i % 2 === 0; //reverse even rows
      const majorAxisParam = majorAxisParams[i];
      const moveX = tx * (-halfDist + majorAxisParam);
      const moveY = ty * (-halfDist + majorAxisParam);
      const [sxNew, syNew, exNew, eyNew] = [sx2 + moveX, sy2 + moveY, ex2 + moveX, ey2 + moveY];
      const positions = getPositionsFromParams(minorAxisParams, sxNew, syNew, exNew, eyNew, nx, ny);
      const resolvedPositions = reverse ? positions.reverse() : positions;
      test.push(...resolvedPositions);
    }

    const loads: Load[] = [];
    for (const pos of test) {
      const {
        location: [x, y],
        normal: [nmx, nmy],
      } = pos;

      if (trimToRoom) {
        if (classifyContainmentVec2PolyLine2(x, y, outline) !== Vec2PolyLine2ContainmentClass.INSIDE) {
          continue;
        }
      }

      // The array was computed in the source room's frame; map each load back to the instance
      // frame (position with translation, normal as a direction) so it lands in the right place.
      // mat3Dot/mat3DotNoTranslate return a shared, reused buffer, so copy each result out before
      // the next call overwrites it.
      const [px, py] = componentMatrix ? mat3Dot(componentMatrix, x, y) : [x, y];
      const [nmxOut, nmyOut] = componentMatrix ? mat3DotNoTranslate(componentMatrix, nmx, nmy) : [nmx, nmy];

      loads.push({
        loadType,
        loadDescriptor,
        position: [px, py],
        normal: [nmxOut, nmyOut],
        levelId: room.level.id,
        sourceRoomHandle: room.roomHandle,
      });
    }

    if (loads.length === 0) {
      return [];
    }

    const candidateFilteringCriteria = candidateFilteringCriteriaNew();
    const { position: startControllerPosition, candidateId: startCandidateId } = await makeControllerSelection(
      controllerPlacementType,
      loads,
      0,
      controllerSelectionFunc,
      type,
      candidateFilteringCriteria,
    );
    const { position: endControllerPosition, candidateId: endCandidateId } = await makeControllerSelection(
      endControllerPlacementType,
      loads,
      loads.length - 1,
      controllerSelectionFunc,
      type,
      candidateFilteringCriteria,
    );

    if (startControllerPosition && endControllerPosition) {
      return [
        {
          levelId: opts.room.level.id,
          loads,
          controllers: [
            {
              type: ControllerType.SWITCH,
              switchType: ControllerSwitchType.THREE_WAY,
              position: startControllerPosition,
              loadIndex: 0,
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
              candidateId: startCandidateId,
              labels: makeBuiltInLabelTable("addToElectricalPlan"),
            },
            {
              type: ControllerType.SWITCH,
              switchType: ControllerSwitchType.THREE_WAY,
              position: endControllerPosition,
              loadIndex: loads.length - 1,
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
              candidateId: endCandidateId,
              labels: makeBuiltInLabelTable("addToElectricalPlan"),
            },
          ],
        },
      ];
    }

    if (!startControllerPosition && !endControllerPosition) {
      return [
        {
          levelId: opts.room.level.id,
          loads,
          controllers: [],
        },
      ];
    }

    if (!endControllerPosition) {
      return [
        {
          levelId: opts.room.level.id,
          loads,
          controllers: [
            {
              type: ControllerType.SWITCH,
              switchType: ControllerSwitchType.STANDARD,
              position: startControllerPosition,
              loadIndex: 0,
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
              candidateId: startCandidateId,
              labels: makeBuiltInLabelTable("addToElectricalPlan"),
            },
          ],
        },
      ];
    }

    return [
      {
        levelId: opts.room.level.id,
        loads,
        controllers: [
          {
            type: ControllerType.SWITCH,
            switchType: ControllerSwitchType.STANDARD,
            position: endControllerPosition,
            loadIndex: loads.length - 1,
            levelId: room.level.id,
            sourceRoomHandle: room.roomHandle,
            candidateId: endCandidateId,
            labels: makeBuiltInLabelTable("addToElectricalPlan"),
          },
        ],
      },
    ];
  };
}

function getMaxLengthEdgeIndex(outline: PolyLine2) {
  let maxLength = -1;
  let maxLengthIndex: number = -1;
  for (let i = 0, l = polyLine2EdgeCount(outline); i < l; i++) {
    const length = polyLine2EdgeLength(outline, i);
    if (length > maxLength) {
      maxLength = length;
      maxLengthIndex = i;
    }
  }
  return maxLengthIndex;
}

function getArraySourcePoint(
  originalOutlineX: number,
  originalOutlineY: number,
  nx: number,
  ny: number,
  outline: PolyLine2,
) {
  let sourceX: number | undefined;
  let sourceY: number | undefined;

  const outlineInts = intersectRay2PolyLine2(outline, originalOutlineX, originalOutlineY, nx, ny);
  ray2PolyLine2IntersectionMakeUnique(outlineInts);
  if (outlineInts.length < 1 || !outlineInts[0]) {
    const x = originalOutlineX;
    const y = originalOutlineY;
    const [xForWidth, yForWidth] = ensureDefined(poleOfInaccessibilityPolygon([outline]));
    const ints = intersectRay2PolyLine2(outline, xForWidth, yForWidth, nx, ny);
    ray2PolyLine2IntersectionMakeUnique(ints);
    if (ints.length < 2) {
      if (ints.length === 1) {
        const { x: intx, y: inty } = ints[0];
        sourceX = intx;
        sourceY = inty;
      } else if (ints.length === 0) {
        sourceX = xForWidth;
        sourceY = yForWidth;
      }
    } else {
      const { x: intx, y: inty } = ints[1];
      [sourceX, sourceY] = polyLine2EdgeEval(polyLine2ByTwoPoints(x, y, intx, inty), 0, 0.5, true);
    }
  } else {
    const { x, y } = outlineInts[0];
    const ints = intersectRay2PolyLine2(outline, x, y, nx, ny);
    ray2PolyLine2IntersectionMakeUnique(ints);
    if (ints.length < 2) {
      if (ints.length === 1) {
        const { x: intx, y: inty } = ints[0];
        sourceX = intx;
        sourceY = inty;
      } else if (ints.length === 0) {
        sourceX = x;
        sourceY = y;
      }
    } else {
      const { x: intx, y: inty } = ints[1];
      [sourceX, sourceY] = polyLine2EdgeEval(polyLine2ByTwoPoints(x, y, intx, inty), 0, 0.5, true);
    }
  }
  return { sourceX, sourceY };
}

function getLineFromOutlineRayIntersections(
  outline: PolyLine2,
  sourceX: number | undefined,
  sourceY: number | undefined,
  tx: number,
  ty: number,
):
  | { invalidIntersection: false; sx: number; sy: number; ex: number; ey: number }
  | { invalidIntersection: true; sx: null; sy: null; ex: null; ey: null } {
  if (sourceX === undefined || sourceY === undefined) {
    return {
      invalidIntersection: true,
      sx: null,
      sy: null,
      ex: null,
      ey: null,
    };
  }
  const ints = intersectRay2PolyLine2(outline, sourceX, sourceY, tx, ty);
  ray2PolyLine2IntersectionMakeUnique(ints);
  const positiveInts: Ray2PolyLine2Intersection[] = [];
  const negativeInts: Ray2PolyLine2Intersection[] = [];
  ints.forEach((x) => {
    if (x.t < 0) {
      negativeInts.push(x);
    } else {
      positiveInts.push(x);
    }
  });
  if (!negativeInts.length || !positiveInts.length) {
    return { invalidIntersection: true, sx: null, sy: null, ex: null, ey: null };
  }

  const { x: sx, y: sy } = negativeInts[negativeInts.length - 1];
  const { x: ex, y: ey } = positiveInts[0];
  return { invalidIntersection: false, sx, sy, ex, ey };
}
